import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertBadge } from "@/components/AlertBadge";
import MyNotificationsDialog from "@/components/MyNotificationsDialog";
import LocationSharer from "@/components/LocationSharer";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { logoSrc } from "@/components/shared";
import SearchBar from "@/components/SearchBar";
import {
  LayoutDashboard,
  Home as HomeIcon,
  CalendarDays,
  Users,
  Sparkles,
  UsersRound,
  Settings as SettingsIcon,
  LogOut,
  BellRing,
  FileText,
  MapPinned,
  Target,
  KeyRound,
  Menu,
  X,
} from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const navItems = [
  { to: "/", label: "Home", icon: HomeIcon, admin: false, end: true },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, admin: false },
  { to: "/jobs", label: "Schedule", icon: CalendarDays, admin: false },
  { to: "/customers", label: "Clients", icon: Users, admin: true },
  { to: "/leads", label: "Leads", icon: Target, admin: true },
  { to: "/map", label: "Live Map", icon: MapPinned, admin: true },
  { to: "/packages", label: "Packages", icon: Sparkles, admin: true },
  { to: "/team", label: "Team", icon: UsersRound, admin: false },
  { to: "/reports", label: "Reports", icon: FileText, admin: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, admin: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const items = navItems.filter((i) => !i.admin || isAdmin);
  const logo = logoSrc(settings);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [alerts, setAlerts] = useState({});
  const location = useLocation();
  const refreshAlerts = () => api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => { refreshAlerts(); setMenuOpen(false); }, [location.pathname]);
  const navAlert = { "/jobs": alerts.schedule?.count, "/customers": alerts.clients?.count, "/leads": alerts.leads?.count,
    "/settings": (alerts.settings?.count || 0) + (alerts.sms?.count || 0) };
  useEffect(() => {
    const open = () => setNotifOpen(true);
    window.addEventListener("open-my-notifications", open);
    return () => window.removeEventListener("open-my-notifications", open);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navList = (prefix) => (
    <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} data-testid={`${prefix}nav-${label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm rounded-xl transition-colors duration-200 ${
              isActive ? "bg-white text-primary font-medium shadow-sm" : "text-white/75 hover:text-white hover:bg-white/15"}`}>
          <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
          {label}
          <AlertBadge count={navAlert[to]} className="ml-auto" testid={`${prefix}nav-badge-${label.toLowerCase()}`} />
        </NavLink>
      ))}
    </nav>
  );
  const accountActions = (prefix, cls, lbl = "") => (
    <>
      {user?.role === "staff" && (
        <button onClick={() => { setMenuOpen(false); setNotifOpen(true); }} data-testid={`${prefix}my-notifications-button`} title="My notifications" className={`${cls} hover:text-brand`}>
          <BellRing className="w-4 h-4" strokeWidth={1.5} /> <span className={lbl}>My notifications</span>
        </button>
      )}
      <button onClick={() => { setMenuOpen(false); setPwOpen(true); }} data-testid={`${prefix}change-password-button`} title="Change password" className={`${cls} hover:text-brand`}>
        <KeyRound className="w-4 h-4" strokeWidth={1.5} /> <span className={lbl}>Change password</span>
      </button>
      <button onClick={handleLogout} data-testid={`${prefix}logout-button`} title="Sign out" className={`${cls} hover:text-destructive`}>
        <LogOut className="w-4 h-4" strokeWidth={1.5} /> <span className={lbl}>Sign out</span>
      </button>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col fixed h-screen">
        <div className="h-24 shrink-0 bg-white border-b border-r border-border/60 flex items-center justify-center px-4" data-testid="sidebar-logo-area">
          <img src="/sterlingtask-logo.png?v=3" alt="SterlingTask" data-testid="sidebar-app-logo" className="h-20 w-auto object-contain" />
        </div>
        <div className="flex-1 min-h-0 bg-brand-gradient text-white flex flex-col pt-6">
          {navList("")}
        </div>
      </aside>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={() => setMenuOpen(false)} />
          <aside className="relative w-72 max-w-[85vw] h-full bg-brand-gradient text-white flex flex-col pb-[env(safe-area-inset-bottom)] shadow-2xl">
            <div className="bg-white pt-[env(safe-area-inset-top)] border-b border-border/60">
              <div className="h-20 px-5 flex items-center justify-between">
                <img src="/sterlingtask-logo.png?v=3" alt="SterlingTask" data-testid="mobile-menu-logo" className="h-16 w-auto object-contain" />
                <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" className="p-2 -mr-2 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="px-6 pt-5 pb-4 text-sm">
              <div className="font-medium" data-testid="mobile-user-name">{user?.name}</div>
              <div className="label-caps text-white/70">{user?.role}</div>
            </div>
            {navList("mobile-")}
            <div className="border-t border-white/20 px-6 py-4 space-y-1">
              {accountActions("mobile-", "w-full flex items-center gap-3 py-2.5 text-sm text-white/85 [&:hover]:text-white")}
            </div>
          </aside>
        </div>
      )}

      <main className="flex-1 lg:ml-64 min-h-screen min-w-0">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur pt-[env(safe-area-inset-top)] lg:static lg:bg-transparent lg:backdrop-blur-0">
          <div className="h-16 lg:h-24 flex items-center gap-3 lg:gap-6 px-4 lg:px-8 border-b border-border/60">
            <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu"
              className="lg:hidden p-2 -ml-2 rounded-xl text-foreground hover:bg-accent/60"><Menu className="w-6 h-6" /></button>
            <img src="/sterlingtask-logo.png?v=3" alt="SterlingTask" data-testid="topbar-app-logo" className="lg:hidden h-10 w-auto object-contain shrink-0" />
            <div className="flex-1 min-w-0 font-serif text-xl lg:text-2xl tracking-tight truncate text-brand-gradient" data-testid="topbar-business-name">
              {settings?.business_name || ""}
            </div>
            <div className="shrink-0 hidden xl:flex justify-center">
              {logo && <img src={logo} alt={settings?.business_name || "Logo"} data-testid="topbar-logo" className="max-h-12 w-auto object-contain" />}
            </div>
            <div className="shrink-0 flex items-center gap-3 lg:gap-4 justify-end">
              <div className="hidden md:block"><SearchBar /></div>
              {user?.role === "staff" && <LocationSharer />}
              <div className="hidden lg:flex items-center gap-3 2xl:gap-4 rounded-full border border-brand/20 bg-[hsl(330_85%_97%)] pl-4 pr-2 py-1.5" data-testid="topbar-account-chip">
                <div className="hidden xl:block text-right leading-tight">
                  <div className="text-sm font-medium truncate max-w-[160px]" data-testid="topbar-user-name">{user?.name}</div>
                  <div className="label-caps text-[hsl(300_60%_45%)]">{user?.role}</div>
                </div>
                <span className="hidden xl:block w-px h-7 bg-brand/20" />
                {accountActions("", "flex items-center gap-2 text-sm text-muted-foreground transition-colors whitespace-nowrap rounded-full px-2 py-1.5 hover:bg-white", "hidden 2xl:inline")}
              </div>
            </div>
          </div>
          <div className="md:hidden px-4 py-2 border-b border-border/60" data-testid="mobile-search"><SearchBar /></div>
        </div>
        <div className="px-4 py-6 sm:px-8 lg:px-12 lg:py-10 max-w-[1400px] animate-fade-in pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <Outlet context={{ alerts, refreshAlerts }} />
        </div>
        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        <MyNotificationsDialog open={notifOpen} onOpenChange={setNotifOpen} />
      </main>
    </div>
  );
}
